import { byAsc, parseDate, sortByComparisons } from "@lyfegen-platform/utils";
import { isDateMatchedByTimeframe } from "apps/platform-client/client/src/utils/agreement-utils";
import {
	ChipFilterState,
	DateFilterState,
	FilterListStateType,
	FilterStateType,
	getDateFilterRange,
	ListFilterState,
} from "../../../../../components/filters/models";
import { BaseAgreement, FinancialDataPointStatus, Treatment } from "../../../../../models/generated/graphql";
import { formatTreatment } from "../../../../../utils/base-agreement-formatters";
import { DocumentMessageModel } from "../../messaging/models";
import getRefundInfo from "./get-refund-info";
import {
	DocumentRefundGroup,
	getCombinedScopeIds,
	getGroupScopes,
	getRefundGroupInfo,
	refundNodeSelector,
	RefundWithState,
} from "./map-values";
import { TrFun } from "apps/platform-client/client/src/utils/types";

type DocumentRecoilKeywordArgs = {
	tenantId: string;
	invoiceId?: string;
};

export const getDocumentPageFiltersRecoilKeyword = ({ tenantId, invoiceId }: DocumentRecoilKeywordArgs) =>
	["documentPageFilter", tenantId, invoiceId].join("-");

export const applySearch = (items: Array<string | undefined>, searchValue: string) => {
	return items.some((item) => item?.toLowerCase()?.includes(searchValue.toLowerCase()));
};

const getRefundSearchValues = (refund: RefundWithState, t: TrFun, currencyCode?: string | null): Array<string> => {
	return Object.values(getRefundInfo(refund, t, currencyCode));
};

const searchGroup = (
	currentGroup: DocumentRefundGroup,
	baseAgreements: Array<BaseAgreement>,
	searchValue: string,
	t: TrFun
): DocumentRefundGroup => {
	const { agreement, caseEntity, currencyCode } = getRefundGroupInfo(currentGroup, baseAgreements);
	const { organizationScopeId, countryScopeId } = getGroupScopes(currentGroup, baseAgreements);

	return {
		...currentGroup,
		refunds: currentGroup.refunds.filter((refund) =>
			applySearch(
				[
					...getRefundSearchValues(refund, t, currencyCode),
					caseEntity?.id,
					caseEntity?.patientInsuranceId,
					baseAgreements.find((a) => a.id === caseEntity?.actualAgreementId)?.name,
					agreement?.name,
					countryScopeId ?? undefined,
					organizationScopeId ?? undefined,
				],
				searchValue
			)
		),
	};
};

const matchListFilter = (listFilter: FilterStateType, passedValue?: string) => {
	const options = (listFilter as ListFilterState).options.filter((option) => option.isSelected);
	const isFilterActive = Boolean(options.length);

	return !isFilterActive || options.find((option) => option.value === passedValue);
};

const matchChipFilter = (chipFilter: FilterStateType, assertion: boolean) => !(chipFilter as ChipFilterState).isActive || assertion;

const matchDateRangeFilter = (dateRangeFilter: DateFilterState, date: string | Date) => {
	if (dateRangeFilter.rangeType === "custom") {
		return (
			(!dateRangeFilter.startValue || parseDate(dateRangeFilter.startValue) <= parseDate(date)) &&
			(!dateRangeFilter.endValue || parseDate(dateRangeFilter.endValue) >= parseDate(date))
		);
	}

	const [startValue, endValue] = getDateFilterRange(dateRangeFilter);

	return isDateMatchedByTimeframe(
		[(startValue && parseDate(startValue)) || null, (endValue && parseDate(endValue)) || null],
		parseDate(date)
	);
};

export const getRefundStatus = (refund: RefundWithState) =>
	refund.documentFinancialDataPointConnection.edges[0].status ?? FinancialDataPointStatus.Undecided;

const filterRefunds = (refunds: Array<RefundWithState>, filters: FilterListStateType, messages: Array<DocumentMessageModel>) => {
	return refunds.filter((refund) => {
		return filters.every((filter) => {
			if (filter.field === "date") {
				return matchDateRangeFilter(
					filter as DateFilterState,
					refund.documentFinancialDataPointConnection.edges[0].node.prescriptionDate
				);
			}

			if (filter.field === "status") {
				return matchListFilter(filter, getRefundStatus(refund));
			}

			if (filter.field === "adjusted") {
				return matchChipFilter(filter, refund.adjustedAmount !== null);
			}

			if (filter.field === "isDelta") {
				return matchChipFilter(filter, refund.documentFinancialDataPointConnection.edges[0].node.isDelta);
			}

			if (filter.field === "wasRemoved") {
				return matchChipFilter(
					filter,
					refund.documentFinancialDataPointConnection.edges[0].status === FinancialDataPointStatus.Removed
				);
			}

			if (filter.field === "pack") {
				return matchListFilter(
					filter,
					formatTreatment(refund.documentFinancialDataPointConnection.edges[0].node.treatment as Treatment)
				);
			}

			if (filter.field === "refundId") {
				return matchListFilter(filter, refund.id);
			}

			if (filter.field === "purchaseId") {
				return matchListFilter(filter, refund.documentFinancialDataPointConnection.edges[0].node.evaluates.purchaseId ?? undefined);
			}

			if (filter.field === "messages") {
				return matchChipFilter(
					filter,
					messages.some((m) => m.refunds.some((r) => r.id === refund.id))
				);
			}

			return true;
		});
	});
};

export const filterGroup = (
	currentGroup: DocumentRefundGroup,
	baseAgreements: Array<BaseAgreement>,
	filters: FilterListStateType,
	messages: Array<DocumentMessageModel>
) => {
	const { caseEntity, agreement } = getRefundGroupInfo(currentGroup, baseAgreements);
	const { countryScopeId } = getGroupScopes(currentGroup, baseAgreements);

	const isGroupFilteredOut = !filters.every((filter) => {
		switch (filter.field) {
			case "scopeId":
				return matchListFilter(filter, getCombinedScopeIds({ countryScopeId }));
			case "agreementId":
				return matchListFilter(filter, agreement?.id);
			case "caseId":
				return matchListFilter(filter, caseEntity?.id);
			case "patientId":
				return matchListFilter(filter, caseEntity?.patientInsuranceId);
		}

		return true;
	});

	if (isGroupFilteredOut) {
		return null;
	}

	return {
		...currentGroup,
		refunds: filterRefunds(currentGroup.refunds, filters, messages),
	};
};

export const sortDocumentData = (data: Array<DocumentRefundGroup>, baseAgreements: Array<BaseAgreement>): Array<DocumentRefundGroup> =>
	data
		.sort(
			sortByComparisons(
				byAsc((group) => {
					const { isCaseless } = getRefundGroupInfo(group, baseAgreements);

					return isCaseless ? -1 : 1;
				}),
				byAsc((group) => {
					const { isCaseless, agreement, caseEntity } = getRefundGroupInfo(group, baseAgreements);
					if (isCaseless) {
						return agreement ? agreement.id : "";
					} else {
						return caseEntity ? caseEntity.id : "";
					}
				})
			)
		)
		.map((group) => {
			return {
				...group,
				refunds: group.refunds.sort(
					sortByComparisons(
						byAsc((refund) => refundNodeSelector(refund).prescriptionDate),
						byAsc((refund) => refundNodeSelector(refund).refundable)
					)
				),
			};
		});

export const handleDocumentPageFilters = (
	data: Array<DocumentRefundGroup>,
	filters: FilterListStateType,
	baseAgreements: Array<BaseAgreement>,
	messages: Array<DocumentMessageModel>,
	search: string,
	t: TrFun
) => {
	const filteredData = data
		.map((group) => filterGroup(group, baseAgreements, filters, messages))
		.filter((group): group is DocumentRefundGroup => Boolean(group));
	const searchedFilteredData = filteredData.map((group) => searchGroup(group, baseAgreements, search, t));
	const sortedSearchedFilteredData = sortDocumentData(searchedFilteredData, baseAgreements);

	return sortedSearchedFilteredData;
};
